import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FilterConfig } from '@/components/common/DataTable/types'
import type { ContractDeploymentRecord } from '@/types'

interface UseDeploymentTableFilteringOptions {
  pageSize: number
  data?: ContractDeploymentRecord[]
}

export const useDeploymentTableFiltering = ({
  pageSize,
  data,
}: UseDeploymentTableFilteringOptions) => {
  const { t } = useTranslation()
  const [limit, setLimit] = useState(pageSize)
  const [deploymentPage, setDeploymentPage] = useState(1)
  const [deploymentSearch, setDeploymentSearch] = useState('')
  const [deploymentStatus, setDeploymentStatus] = useState('')
  const [deploymentVerificationStatus, setDeploymentVerificationStatus] = useState('')
  const [deploymentChainId, setDeploymentChainId] = useState('')
  const [deploymentFilters, setDeploymentFilters] = useState<Record<string, any>>({})

  const filteredDeploymentData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return { data: [], pagination: { page: 1, limit: 20, total: 0, pages: 0 } }
    }

    let filtered = [...data]

    if (deploymentSearch.trim()) {
      const searchLower = deploymentSearch.toLowerCase()
      filtered = filtered.filter(
        record =>
          record &&
          (record.order_id?.toLowerCase().includes(searchLower) ||
            record.orderId?.toLowerCase().includes(searchLower) ||
            record.payment_address?.toLowerCase().includes(searchLower) ||
            record.predicted_contract_address?.toLowerCase().includes(searchLower) ||
            record.deployed_contract_address?.toLowerCase().includes(searchLower) ||
            record.deployment_tx_hash?.toLowerCase().includes(searchLower))
      )
    }

    if (deploymentStatus) {
      filtered = filtered.filter(record => record && record.status_id === deploymentStatus)
    }

    if (deploymentVerificationStatus) {
      filtered = filtered.filter(
        record => record && record.address_verification_status_id === deploymentVerificationStatus
      )
    }

    if (deploymentChainId) {
      filtered = filtered.filter(record => record && record.registry_id === deploymentChainId)
    }

    const total = filtered.length
    const pages = Math.ceil(total / limit)
    const startIndex = (deploymentPage - 1) * limit
    const endIndex = startIndex + limit
    const paginatedData = filtered.slice(startIndex, endIndex)

    return {
      data: paginatedData,
      pagination: {
        page: deploymentPage,
        limit,
        total,
        pages,
      },
    }
  }, [
    data,
    deploymentChainId,
    deploymentPage,
    deploymentSearch,
    deploymentStatus,
    deploymentVerificationStatus,
    limit,
  ])

  const deploymentFilterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        key: 'search',
        type: 'text',
        label: 'Search',
        placeholder: t('contractRegistry.deployment.searchPlaceholder'),
        width: '280px',
      },
      {
        key: 'deploymentStatus',
        type: 'select',
        label: 'Status',
        placeholder: t('contractRegistry.deployment.allStatuses'),
        width: '160px',
        options: [
          { value: 'successful', label: t('contractRegistry.deployment.status.successful') },
          { value: 'failed', label: t('contractRegistry.deployment.status.failed') },
          { value: 'reverted', label: t('contractRegistry.deployment.status.reverted') },
          { value: 'pending', label: t('contractRegistry.deployment.status.pending') },
        ],
      },
      {
        key: 'deploymentVerificationStatus',
        type: 'select',
        label: 'Verification',
        placeholder: t('contractRegistry.deployment.allVerification'),
        width: '140px',
        options: [
          { value: 'avs_verified', label: t('contractRegistry.deployment.verification.verified') },
          { value: 'avs_pending', label: t('contractRegistry.deployment.verification.pending') },
          { value: 'avs_failed', label: t('contractRegistry.deployment.verification.failed') },
        ],
      },
    ],
    [t]
  )

  const handleDeploymentFiltersChange = useCallback((newFilters: Record<string, any>) => {
    setDeploymentFilters(newFilters)
    setDeploymentSearch(newFilters.search || '')
    setDeploymentStatus(newFilters.deploymentStatus || '')
    setDeploymentVerificationStatus(newFilters.deploymentVerificationStatus || '')
    setDeploymentChainId(newFilters.deploymentChainId || '')
    setDeploymentPage(1)
  }, [])

  const handleDeploymentPageSizeChange = useCallback((newPageSize: number) => {
    setLimit(newPageSize)
    setDeploymentPage(1)
  }, [])

  const deploymentPaginationConfig = filteredDeploymentData.pagination
    ? {
        page: deploymentPage,
        pageSize: limit,
        total: filteredDeploymentData.pagination.total,
        pageSizeOptions: [10, 20, 50, 100],
      }
    : undefined

  return {
    deploymentFilters,
    deploymentFilterConfigs,
    deploymentPaginationConfig,
    filteredDeploymentData,
    setDeploymentPage,
    handleDeploymentFiltersChange,
    handleDeploymentPageSizeChange,
  }
}
